import {
  MIQAAT_VENUE_DEFAULT_RADIUS_METERS,
  parseOptionalNumber,
} from "./transform-miqaat-venue-payload";

/** Default map center when a venue has no coords yet (Pune). */
export const MIQAAT_VENUE_MAPS_DEFAULT_CENTER = {
  lat: 18.5204,
  lng: 73.8567,
} as const;

export const MIQAAT_VENUE_MAPS_DEFAULT_ZOOM = 15;

/** Minimum framing radius so a place selection isn’t over-zoomed. */
export const MIQAAT_VENUE_MAPS_MIN_FIT_RADIUS_M = 100;

/** Padding (px) passed to `map.fitBounds` after place search. */
export const MIQAAT_VENUE_MAPS_FIT_PADDING_PX = 48;

/**
 * Soft location bias around Pune. Places Autocomplete circle bias max is 50 km;
 * India-wide restriction still comes from `includedRegionCodes`.
 */
export const MIQAAT_VENUE_MAPS_LOCATION_BIAS_RADIUS_M = 50_000;

export const MIQAAT_VENUE_MAPS_INCLUDED_REGION_CODES = ["in"] as const;

export type VenueLatLng = { lat: number; lng: number };

/** Browser Maps JS key (`VITE_GOOGLE_MAPS_API_KEY`). No key in the API. */
export function resolveGoogleMapsApiKey(
  envValue: string | undefined = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
): string | null {
  const trimmed = envValue?.trim();
  return trimmed ? trimmed : null;
}

export function roundVenueCoord(value: number, digits = 6): number {
  if (!Number.isFinite(value)) {
    return value;
  }
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

export function parseVenueLatLng(latitude: unknown, longitude: unknown): VenueLatLng | null {
  const lat = parseOptionalNumber(latitude);
  const lng = parseOptionalNumber(longitude);
  if (lat === undefined || lng === undefined) {
    return null;
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  return { lat, lng };
}

/**
 * Autocomplete selection → form fields. Always writes lat/lng; fills `name` from
 * the place display name when present (ops can edit name afterward).
 */
export function buildVenueLocationFromPlace(input: {
  latitude: number;
  longitude: number;
  placeDisplayName?: string | null;
}): { latitude: number; longitude: number; name?: string } {
  const latitude = roundVenueCoord(input.latitude);
  const longitude = roundVenueCoord(input.longitude);
  const placeName = input.placeDisplayName?.trim();
  if (placeName) {
    return { latitude, longitude, name: placeName };
  }
  return { latitude, longitude };
}

/** Pin drag / map click → lat/lng only (name unchanged). */
export function buildVenueCoordsFromPin(input: { latitude: number; longitude: number }): {
  latitude: number;
  longitude: number;
} {
  return {
    latitude: roundVenueCoord(input.latitude),
    longitude: roundVenueCoord(input.longitude),
  };
}

/** Positive finite radius for the optional map circle overlay; otherwise null. */
export function resolveRadiusCircleMeters(radiusMeters: unknown): number | null {
  const n = parseOptionalNumber(radiusMeters);
  if (n === undefined || n <= 0) {
    return null;
  }
  return n;
}

/**
 * Lat/lng bounds that contain a circle of `radiusMeters` around `center`
 * (approx; good enough for map.fitBounds after place search).
 */
export function venueMapFitBoundsLiteral(
  center: VenueLatLng,
  radiusMeters: number
): { north: number; south: number; east: number; west: number } {
  const metersPerDegLat = 111_320;
  const latDelta = radiusMeters / metersPerDegLat;
  const cosLat = Math.cos((center.lat * Math.PI) / 180);
  const metersPerDegLng = metersPerDegLat * Math.max(Math.abs(cosLat), 1e-6);
  const lngDelta = radiusMeters / metersPerDegLng;
  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

/** Radius used to frame the map after confirming a place (form radius or default). */
export function resolveVenueMapFitRadiusMeters(radiusMeters: unknown): number {
  const fromForm = resolveRadiusCircleMeters(radiusMeters);
  const base = fromForm ?? MIQAAT_VENUE_DEFAULT_RADIUS_METERS;
  return Math.max(base, MIQAAT_VENUE_MAPS_MIN_FIT_RADIUS_M);
}
